import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import * as motion from 'motion/react-m';
import Link from 'next/link';

import {DEFAULT_NAV_ICON_VARIANTS, NAV_ICON_TRANSFORM_ORIGIN, NAV_ICON_VARIANTS} from 'UI/animations/navIcons';

/** A single tab definition for the NavigationBar. */
export interface NavTab {
    /** The route to navigate to when the tab is pressed. */
    href: string;
    /** The icon rendered above the label. Should be a 24×24 SVG. */
    icon: ReactNode;
    /** Unique identifier for the tab. */
    key: string;
    /** The visible label below the icon. */
    label: string;
}

/**
 * The props for the `NavigationBar` component.
 * Configures a bottom tab bar with tab items linked to routes.
 */
interface ComponentProps {
    /** The key of the currently active tab. */
    activeTab: string;
    /** The tab items to render. */
    tabs: NavTab[];
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'NavigationBar'
     */
    testId?: string;
}

/**
 * A bottom navigation bar with tab icons and labels.
 * Matches the Figma NavigationBar at full-width × 64px, surfBg background.
 * Active tab shows a primaryPale circle indicator behind the icon and
 * primaryDefault coloring with SemiBold label.
 *
 * @param props           The component props.
 * @param props.activeTab The key of the currently active tab.
 * @param props.tabs      The tab items to render.
 * @param props.testId    An optional test ID used for automation selectors. Defaults to `'NavigationBar'`.
 * @returns A JSX element rendering the NavigationBar.
 *
 * @example
 * ```tsx
 * <NavigationBar
 *     activeTab="home"
 *     tabs={[
 *         {href: '/', icon: <ExploreIcon />, key: 'home', label: 'Start'},
 *         {href: '/login', icon: <AccountCircleIcon />, key: 'login', label: 'Anmelden'}
 *     ]}
 * />
 * ```
 */
const NavigationBar = ({
    activeTab,
    tabs,
    testId = 'NavigationBar'
}: ComponentProps) => (
    <Bar data-cy={testId}>
        {tabs.map(tab => {
            const isActive = tab.key === activeTab;

            return (
                <Tab
                    key={tab.key}
                    data-cy={`${testId}-tab-${tab.key}`}
                    href={tab.href}
                >
                    <IconArea>
                        {isActive && (
                            <ActiveIndicator
                                layoutId="navigationBarActiveIndicator"
                                transition={{
                                    damping: 30,
                                    stiffness: 500,
                                    type: 'spring'
                                }}
                            />
                        )}
                        <IconWrapper
                            $isActive={isActive}
                            animate={isActive ? 'active' : 'inactive'}
                            initial={false}
                            style={{transformOrigin: NAV_ICON_TRANSFORM_ORIGIN[tab.key] ?? 'center'}}
                            variants={NAV_ICON_VARIANTS[tab.key] ?? DEFAULT_NAV_ICON_VARIANTS}
                        >
                            {tab.icon}
                        </IconWrapper>
                    </IconArea>
                    {/* ⚠️ Figma: 9px — kleiner als alle P-Stufen, custom styles nötig */}
                    <TabLabel $isActive={isActive}>{tab.label}</TabLabel>
                </Tab>
            );
        })}
    </Bar>
);

NavigationBar.displayName = 'NavigationBar';

export {NavigationBar};

// ── Styled ──────────────────────────────────────────────────

interface ActiveProps {
    /** Whether the tab item is currently active. */
    $isActive: boolean;
}

const Bar = styled.nav`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceBackground};
    display: flex;
    height: 6.4rem;
    justify-content: space-around;
    padding: 0 ${spacing(2)};
    width: 100%;
`;

const Tab = styled(Link)`
    align-items: center;
    color: inherit;
    cursor: pointer;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${spacing(0.5)};
    justify-content: center;
    padding: ${spacing(1)} 0;
    text-decoration: none;
`;

const IconArea = styled.div`
    align-items: center;
    display: flex;
    height: 3.2rem;
    justify-content: center;
    position: relative;
    width: 3.2rem;
`;

const ActiveIndicator = styled(motion.div)`
    background: ${({theme}) => theme.colors.primaryPale};
    border-radius: 50%;
    height: 3.2rem;
    left: 0;
    position: absolute;
    top: 0;
    width: 3.2rem;
`;

const IconWrapper = styled(motion.span)<ActiveProps>`
    align-items: center;
    color: ${({$isActive, theme}) => (
        $isActive
            ? theme.colors.primaryDefault
            : theme.colors.inkTertiary
    )};
    display: flex;
    height: 2.4rem;
    justify-content: center;
    position: relative;
    transition: color 0.2s ease;
    width: 2.4rem;
    z-index: 1;

    & > svg {
        height: 100%;
        width: 100%;
    }
`;

const TabLabel = styled.span<ActiveProps>`
    color: ${({$isActive, theme}) => (
        $isActive
            ? theme.colors.primaryDefault
            : theme.colors.inkTertiary
    )};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 0.9rem;
    font-weight: ${({$isActive}) => ($isActive ? '600' : '400')};
    line-height: 1;
    transition: color 0.2s ease;
`;